/**
 * Dictionary list configuration.
 *
 * Defines available dictionaries and groups them logically.
 * The GROUPED_DICTLIST has dynamic keys (dictionary IDs) and values (dictionary metadata).
 * Groups can contain items, which are flattened into DICTLIST.
 */

/** Common properties for all dictionary entries */
interface BaseDictEntry {
  label: string;
  about?: string;
  abbreviations?: string;
  public: boolean;
  listCredits?: boolean;
  language: string[];
  highlight?: string;
  mergeLines?: boolean;
  audioId?: string;
  webOnly?: boolean;
  groupId?: string; // Added by build process for flat entries
  preformattedLinebreaks?: boolean;
  scanId?: string;
  scanInfo?: {
    offset: number;
    display_pageadjust: number;
    min_page: number;
    max_page: number;
    width: number;
    height: number;
  };
  linkText?: string;
}

/** A single dictionary entry (non-group) */
export interface DictEntry extends BaseDictEntry {}

/** A group of dictionaries */
interface DictGroup extends BaseDictEntry {
  type: 'group';
  items: {
    [key: string]: DictEntry;
  };
}

/** A dictionary entry that can be either a group or a single entry */
type DictEntryType = DictEntry | DictGroup;

/** Grouped dictionary list with dynamic keys for group/dictionary names */
interface GroupedDictListType {
  [key: string]: DictEntryType;
}

/** Flattened dictionary list (groups removed, items extracted) */
export interface DictListType {
  [key: string]: DictEntry & { groupId?: string };
}

export const GROUPED_DICTLIST: GroupedDictListType = {
  "TsepakRigdzin": {
    label: 'Tsepak Rigdzin',
    highlight: '\\[([^\\]]*)\\]',
    about: 'Tibetan-English Dictionary of Buddhist Terms|Revised and Enlarged Edition|Tsepak Rigdzin|Library of Tibetan Works and Archives|ISBN: 81-85102-88-0',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "GaengWetzel": {
    label: 'Gäng / Wetzel',
    about: 'Buddhist Terms|Multilingual Version|Edited by Peter Gäng and Sylvia Wetzel|Buddhist Academy Berlin Brandenburg|June 2004|Source:  http://www.buddhistische-akademie-bb.de/pdf/BuddhistTerms.pdf',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "_HeartOfTibetanLanguage": {
    type: 'group',
    label: 'Heart of Tibetan Language',
    about: 'Vocabulary from the supplementary Anki learning cards for: The Heart of Tibetan Language|by Franziska Oertle|Dharma Publishing (dharmapublishing.com)|Volume 1: ISBN: 978-0-89800-233-1|Volume 2: ISBN: 978-0-89800-308-6|Volume 3: ISBN: 978-0-89800-382-6',
    public: true,
    language: ["tib", "<->", "en"],
    items: {
      "hotl1": {
        label: 'Heart of Tibetan Language 1',
        about: 'Vocabulary from the supplementary Anki learning cards for: The Heart of Tibetan Language|by Franziska Oertle|Dharma Publishing (dharmapublishing.com)|Volume 1: ISBN: 978-0-89800-233-1|Volume 2: ISBN: 978-0-89800-308-6|Volume 3: ISBN: 978-0-89800-382-6',
        public: true,
        listCredits: true,
        audioId: "hotl1",
        language: ["tib", "<->", "en"]
      },
      "hotl2": {
        label: 'Heart of Tibetan Language 2',
        about: 'Vocabulary from the supplementary Anki learning cards for: The Heart of Tibetan Language|by Franziska Oertle|Dharma Publishing (dharmapublishing.com)|Volume 1: ISBN: 978-0-89800-233-1|Volume 2: ISBN: 978-0-89800-308-6|Volume 3: ISBN: 978-0-89800-382-6',
        public: true,
        audioId: "hotl2",
        language: ["tib", "<->", "en"]
      },
      "hotl3": {
        label: 'Heart of Tibetan Language 3',
        about: 'Vocabulary from the supplementary Anki learning cards for: The Heart of Tibetan Language|by Franziska Oertle|Dharma Publishing (dharmapublishing.com)|Volume 1: ISBN: 978-0-89800-233-1|Volume 2: ISBN: 978-0-89800-308-6|Volume 3: ISBN: 978-0-89800-382-6',
        public: true,
        audioId: "hotl3",
        language: ["tib", "<->", "en"]
      }
    }
  },
  "Drungtso": {
    label: 'Drungtso - Medical &amp; Astro Terms',
    highlight: '(&lt;[^&]*&gt;)',
    about: 'Tibetan-English Dictionary of Tibetan Medicine and Astrology|First Edition, Drungtso Publications 1999| Dr. Tsering Thakchoe Drungtso | &amp; Mrs. Tsering Dolma Drungtso',
    public: false,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "Illuminator_x": {
    label: 'Illuminator (please only use this dictionary if you bought the original version from www.pktc.org!)',
    highlight: '(&lt;[^ ]*&gt;)',
    about: 'The Illuminator Tibetan-English Encyclopaedic Dictionary|Version 5.23, January 2014|Tony Duff 2000-2014, All rights reserved|If you use this dictionary, please buy it at: www.pktc.org/pktc',
    abbreviations: 'Illuminator',
    public: false,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "TibTermProject": {
    label: 'Tibetan Terminology Project: Glossary of Standardised Terms',
    about: 'Tibetan Terminology Project: Glossary of Standardised Terms| Department of Education, Central Tibetan Administation, India, 2013| Chief editor: Acharya Karma Monlam | Assistant editors: Ogyen Tenzin, Yeshi Tenzin, Tsering Dhondup |Database consultant: Pema Zomkyi | Software consultants: ven. Lobsang Monlam, Gedun Dhonyoe | online version: https://tibterminology.net/',
    abbreviations: 'TibTermProject',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "Hopkins2015": {
    label: 'Hopkins 2015',
    about: 'The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
    abbreviations: 'Hopkins',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "Bialek": {
    label: 'Bialek',
    about: 'Glossary entries from Joanna Bialek: A Textbook in Classical Tibetan|Routledge (2022)|ISBN: 9781032123561|Book available at https://www.routledge.com/A-Textbook-in-Classical-Tibetan/Bialek/p/book/9781032123561',
    public: true,
    listCredits: true,
    abbreviations: "Bialek",
    language: ["tib", "<->", "en"]
  },
  "CommonTerms-Lin": {
    label: 'Chung-An Lin',
    about: 'Common Chinese-Tibetan-Sanskrit-English Buddhist Terminology|Compiled by Chung-An Lin, assisted by Hou-Wha Wang|2008|www.insights.org.tw',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "_RangjungYeshe": {
    type: 'group',
    label: 'Rangjung Yeshe',
    about: 'Rangjung Yeshe Dictionary|Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 by Erik Pema Kunsang (2003)|online version: http://rywiki.tsadra.org',
    public: true,
    language: ["tib", "<->", "en"],
    items: {
      "RangjungYeshe": {
        label: 'Rangjung Yeshe',
        about: 'Rangjung Yeshe Dictionary|Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 by Erik Pema Kunsang (2003)|online version: http://rywiki.tsadra.org',
        abbreviations: 'RangjungYeshe',
        public: true,
        listCredits: true,
        language: ["tib", "<->", "en"]
      },
      "GatewayToKnowledge": {
        label: 'Glossary for Mipham Rinpoche\'s Gateway to Knowledge, Vol. 1 (Rangjung Yeshe)',
        mergeLines: true,
        about: 'Glossary for Mipham Rinpoche\'s Gateway to Knowledge, Vol. 1|Rangjung Yeshe Publications|Source: www.rangjung.com/gateway/KJ-main.htm',
        public: true,
        listCredits: true,
        language: ["tib", "<->", "en"]
      }
    }
  },
  "_Berzin": {
    type: 'group',
    label: 'Berzin',
    about: 'Dr. Alexander Berzin\'s English-Tibetan-Sanskrit Glossary|These entries are from the glossary of www.berzinarchives.com',
    public: true,
    language: ["tib", "<->", "en"],

    items: {
      "Berzin": {
      label: 'Berzin',
      mergeLines: true,
      about: 'Dr. Alexander Berzin\'s English-Tibetan-Sanskrit Glossary|These entries are from the glossary of www.berzinarchives.com',
      public: true,
      listCredits: true,
      language: ["tib", "<->", "en"]
    },
    "Berzin-Def": {
      label: 'Berzin Defi&shy;nitions',
      about: 'Source: Alexander Berzin\'s English-Tibetan-Sanskrit Glossary|These entries are from the glossary of www.berzinarchives.com',
      public: true,
      language: ["tib", "<->", "en"]
    }
  }
  },
  "_Hopkins-Definitions-English": {
    type: 'group',
    label: 'Hopkins and Hackett Defi&shy;nitions 2015',
    about: 'English Definitions by Jeffrey Hopkins and Paul Hackett as well as additional comments, divisions, others\' English|Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
    public: true,
    language: ["tib", "<->", "en"],
    items: {
      "Hopkins-Def2015": {
        label: 'Hopkins Defi&shy;nitions 2015',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "Hackett-Def2015": {
        label: 'Hackett Defi&shy;nitions 2015',
        about: 'Definitions by Paul Hackett|Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "JongbokYi": {
        label: 'Jongbok Yi',
        about: 'Definitions by Jongbok Yi',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "->", "en"]
      },
      "Hopkins-Comment": {
        label: 'Hopkins Comment 1992',
        about: 'Source: Jeffrey Hopkins\' Tibetan-Sanskrit-English Dictionary|Version 2.0.0, 1992|Formulator and Editor: Jeffrey Hopkins|Contributors: Joe Wilson, Craig Preston, John Powers, Nathaniel Garson, Paul Hackett, Andres Montano',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "Hopkins-Divisions2015": {
        label: 'Hopkins Divi&shy;sions 2015',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "Hopkins-Examples": {
        label: 'Hopkins Examples 1992',
        about: 'Source: Jeffrey Hopkins\' Tibetan-Sanskrit-English Dictionary|Version 2.0.0, 1992|Formulator and Editor: Jeffrey Hopkins|Contributors: Joe Wilson, Craig Preston, John Powers, Nathaniel Garson, Paul Hackett, Andres Montano',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "Hopkins-othersEnglish2015": {
        label: 'Hopkins others\' English 2015',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      },
      "Hopkins-Synonyms1992": {
        label: 'Hopkins Synonyms 1992',
        about: 'Source: Jeffrey Hopkins\' Tibetan-Sanskrit-English Dictionary|Version 2.0.0, 1992|Formulator and Editor: Jeffrey Hopkins|Contributors: Joe Wilson, Craig Preston, John Powers, Nathaniel Garson, Paul Hackett, Andres Montano',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib", "<->", "en"]
      }
    }
  },
  "84000Dict": {
    label: '84000 Glossary',
    about: '84000 Glossary|English terms from the Glossary of the 84000 translation project. http://www.84000.co/',
    public: true,
    abbreviations: '84000',
    highlight: '(&lt;[^ ]*&gt;)',
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "84000Definitions": {
    label: '84000 Glossary-Definitions',
    about: 'English definitions from the Glossary of the 84000 translation project. http://www.84000.co/',
    abbreviations: '84000',
    highlight: '(&lt;[^ ]*&gt;)',
    public: true,
    language: ["tib", "<->", "en"]
  },
  "ChandraDas_x": {
    label: 'Chandra Das (Please only use this dictionary if you bought the electronic Chandra Das edition from www.pktc.org!)',
    about: 'New Electronic Ediction of Sarat Chandra Das\' Tibetan-English Dictionary|version 1.023, 21st September, 2005|Padma Karpo Translation Committee, 1998-2005|If you use this dictionary, please buy it at: www.pktc.org/pktc',
    abbreviations: 'ChandraDas',
    public: false,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "ThomasDoctor": {
    label: 'Thomas Doctor',
    about: 'Thomas Doctor\'s Tibetan-English terms|Source: Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 (2003)|online version: http://rywiki.tsadra.org',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "JimValby": {
    label: 'James Valby',
    about: 'James Valby\'s Tibetan-English Dictionary. © James Valby|Source: Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 (2003)|online version: http://rywiki.tsadra.org',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "IvesWaldo": {
    label: 'Ives Waldo',
    about: 'Ives Waldo\'s glossary compilation. © Ives Waldo|Source: Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 (2003)|online version: http://rywiki.tsadra.org',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "DanMartin": {
    label: 'Dan Martin',
    about: 'Dan Martin: “Tibetan Vocabulary.”, April 2003|This is meant to be a word-index more than a dictionary, and technical, idiosyncratic, and obsolete usages and meanings are given priority over more common ones which may readily be found in the available dictionaries.  To some degree, the content reflects my own research focus on 11th and 12th century texts.|Source: Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 (2003)|online version: http://rywiki.tsadra.org',
    abbreviations: 'DanMartin',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "RichardBarron": {
    label: 'Richard Barron',
    about: 'Richard Barron\'s glossary. © Copyright 2002 by Turquoise Dragon Media Services. Source: Rangjung Yeshe Tibetan-English Dharma Dictionary 3.0 (2003)|online version: http://rywiki.tsadra.org',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "tibetanlanguage-school": {
    label: 'Tibetan Language School: Supplementary Vocabulary',
    about: 'Vocabulary from the Tibetan Language School website|https://tibetanlanguage.school/resources/vocabulary-supplement/|by Nick Prior|This vocabulary contains additional colloquial terms that are not available from other sources in this dictionary application. Many thanks to Nick Prior for compiling of this vocabulary and making it available.', 
    public: true,
    listCredits: true,
    language: ["tib", "->", "en"]
  },
  "Jaeschke_Scan": {
    label: 'Jäschke (scanned dictionary)',
    about: 'Heinrich August Jäschke|A Tibetan-English dictionary with special reference to the prevailing dialects; to which is added an English-Tibetan vocabulary (1881)||https://archive.org/details/tibetanenglishdi00jsuoft/||By opening a dictionary entry, the program will try to bring you to the scanned page which most likely will contain that term. The dictionary application does not have a complete list of all terms  in the scanned document so you have to check yourself if the term is present or not. Also, since the sort order in the printed dictionary is not always perfect, you sometimes have to jump a few pages back or ahead to find the desired term. Nevertheless this hopefully helps a lot with making the data of this dictionary available.||Huge thanks go to Octave Boczkowski who came up with this wonderful idea for integrating scanned dictionaries, provided the code for determining the most likely page number for a term, and went through the work of mediculously indexing the first and last head-words on each page in the dictionaries by Jäschke and Chandra Das.|Furthermore, sincere thanks go to mKhan po sKal bzang for indexing all the headwords in Jäschke, thereby further improving the accuracy of the integration',
    public: true,
    webOnly: true,
    scanId: "jaeschke",
    scanInfo: {
      offset: 0,
      display_pageadjust: -26,
      min_page: 3,
      max_page: 697,
      width: 907,
      height: 1407
    },
    linkText: "Open the corresponding page in Jäschke's Tibetan-English Dictionary to display the headword or, if applicable, a subentry.",
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "ChandraDas_Scan": {
    label: 'Chandra Das (scanned dictionary)',
    about: 'Sarat Chandra Das|A Tibetan English Dictionary (1902)||https://archive.org/details/tibetanenglishdi00dassuoft/||By opening an dictionary entry, the program will try to bring you to the scanned page which most likely will contain that term. ||Huge thanks go to Octave Boczkowski who came up with this wonderful idea for integrating scanned dictionaries, provided the code for determining the most likely page number for a term, and went through the work of mediculously indexing the first and last head-words in the dictionaries by Jäschke and Chandra Das.',
    public: true,
    webOnly: true,
    scanId: "das",
    scanInfo: {
      offset: 40,
      display_pageadjust: 0,
      min_page: 5,
      max_page: 1393,
      width: Math.round(1157 * 1.25),
      height: Math.round(1482 * 1.25)
    },
    linkText: "Open the corresponding page in Chandra Das's Tibetan-English dictionary.",
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "MastersProgramGlossary": {
    label: 'FPMT Masters Program Glossary for Abhisamaya&shy;alamkara',
    about: 'This glossary was compiled from Material of the FPMT Masters Program 1998 in Pomaia (Italy)',
    public: false,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "sgra_bye_brag_tu_rtogs_byed_chen_mo": {
    label: 'sgra bye brag tu rtogs byed chen mo skad bzhi shan sbyar ma (Mahā&shy;vyutpatti with added English)',
    about: "Tibetan-Sanskrit-English-Chinese Dictionary of Buddhism: An Edition and Translation of the Mahāvyutpatti|Author: bu chung tshe ring|Lhasa: Xizang renmin chubanshe 西藏人民出版社 / bod ljongs mi dmangs dpe skrun khang (Tibetan People's Publishing House), 2015. ISBN 9787223046312.|Scan available at archive.org: https://archive.org/details/bdrc-W3CN6915/",
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en", "<->", "skt"]
  },
  "FPMT-middling-lamrim": {
    label: 'FPMT Glossary for Middling Lamrim (work in progress)',
    mergeLines: true,
    about: 'This glossary was compiled by the FPMT and includes key technical terms for Tsong-kha-pa\'s Middling Lamrim',
    public: false,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "ComputerTerms": {
    label: 'Computer terms',
    about: 'Tibetan Computer Terms|Source: Standardizing Tibetan Terms of IT by the China Tibetology Research Center, 2006. This list was compiled by the Project of Standardizing Tibetan Terms of Information Technology, at the China National Center for Tibetan Studies. It was eventually published as The Dictionary of Tibetan Information Technology Terminologies. (Chinese equivalents and English-only entries were removed)',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },
  "Misc": {
    label: 'Miscellaneous definitions',
    about: 'Mixed definitions from various sources',
    public: true,
    language: ["tib", "<->", "en"]
  },
  "LobsangMonlam": {
    label: 'Lobsang Monlam (English)',
    about: 'Lobsang Monlam Dictionary (English)|These entries were taken from an old version of Lobsang Monlam\'s dictionary. See http://www.monlamit.org/ for the original program and https://github.com/iamironrabbit/monlam-dictionary for the code and database.',
    public: false,
    language: ["tib", "<->", "en"]
  },
  "Verbinator": {
    label: 'Verbinator Verb Dictionary',
    highlight: '((?:Past|Present|Future|Imperative|Meaning): )',
    abbreviations: 'Verbinator',
    about: '\'Verbinator\' Tibetan Verb Dictionary (Verbinator 2000)|Taken from: Hill, Nathan (2010)|A Lexicon of Tibetan Verb Stems as Reported by the Grammatical Tradition.|Munich: Bayerische Akademie der Wissenschaften. (Studia Tibetica)|ISBN 978-3-7696-1004-8|If you want to support the author and the publisher please consider buying this book (regular price: 58 EUR plus shipping). If you have trouble with ordering it, feel free send a mail to dictionary@christian-steinert.de and I will try to help you.',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "en"]
  },

  "_Hopkins-Tibetan": {
    type: 'group',
    label: 'Hopkins Tibetan 2015',
    about: 'Tibetan Definitions, synonyms and examples by Jeffrey Hopkins et al|Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
    public: true,
    language: ["tib"],
    items: {
      "Hopkins-TibetanDefinitions2015": {
        label: 'Hopkins Defi&shy;nitions 2015',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      },
      "Hopkins-TibetanSynonyms2015": {
        label: 'Hopkins Synonyms 2015',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      },
      "Hopkins-TibetanSynonyms1992": {
        label: 'Hopkins Synonyms 1992',
        about: 'Source: Jeffrey Hopkins\' Tibetan-Sanskrit-English Dictionary|Version 2.0.0, 1992|Formulator and Editor: Jeffrey Hopkins|Contributors: Joe Wilson, Craig Preston, John Powers, Nathaniel Garson, Paul Hackett, Andres Montano',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      },
      "Hopkins-TibetanTenses2015": {
        label: 'Hopkins Tenses 2015',
        about: 'The order of the verb tenses in this dictionary is: future, present, past, imperative|Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      },
      "Hopkins-DivisionsTib2015": {
        label: 'Hopkins Divi&shy;sions 2015 ',
        about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      },
      "Hopkins-ExamplesTib": {
        label: 'Hopkins Examples 1992',
        about: 'Source: Jeffrey Hopkins\' Tibetan-Sanskrit-English Dictionary|Version 2.0.0, 1992|Formulator and Editor: Jeffrey Hopkins|Contributors: Joe Wilson, Craig Preston, John Powers, Nathaniel Garson, Paul Hackett, Andres Montano',
        abbreviations: 'Hopkins',
        public: true,
        language: ["tib"]
      }
    }
  },
  "84000Synonyms": {
    label: '84000 Tibetan Synonyms',
    about: 'Synonymous Tibetan terms from the Glossary of the 84000 translation project. http://www.84000.co/',
    abbreviations: '84000',
    highlight: '(&lt;[^ ]*&gt;)',
    public: true,
    language: ["tib"]
  },
  "LaineAbbreviations": {
    label: 'Tibetan Abbreviations',
    about: 'Tibetan abbreviations|Compiled, formatted and kindly shared by Bruno Lainé (Version: September 2021)|http://www.rkts.org/',
    abbreviations: 'LaineAbbreviations',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "Sera-Textbook-Definitions": {
    label: 'Sera Textbook Definitions',
    about: 'Sera Textbook Definitions|This is a collection of various Tibetan definitions from major textbook authors of Sera Monastery|The definitions are extracted from material of the Asian Classics Input Project so there will likely be typos and mistakes',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "GesheChodrak-Tib_x": {
    label: 'Geshe Chödrak - Please only use this dictionary if you bought the original version from www.pktc.org!',
    about: 'Geshe Chödrak Dictionary|“Written Signals - Names and Phrases Made Clear”\nGeshe Chodrak\'s (Tibetan) Dictionary\nVersion 1.20 30th September, 2005\nPadma Karpo Translation Committee\n© TONY DUFF 2000-2004. All rights reserved.\nIf you use this dictionary, please buy it at: www.pktc.org/pktc',
    public: false,
    listCredits: true,
    language: ["tib"]
  },
  "tshig-mdzod-chen-mo-Tib": {
    label: 'bod rgya tshig mdzod chen mo',
    about: 'Bod rgya tshig mdzod chen mo / 藏汉大辞典|Author: Zhang Yisun|Beijing: Mi dmangs dpe skrun khang, 1985',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "dung-dkar-tshig-mdzod-chen-mo-Tib": {
    label: 'dung dkar tshig mdzod chen mo',
    about: "Dung dkar tshig mdzod chen mo|author: dung dkar blo bzang 'phrin las|Beijing: zhongguo zangxue chubanshe 中国藏学出版社 / krung go'i bod rig pa dpe skrun khang (China’s Tibetology Publishing House), 2002",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "dag_tshig_gsar_bsgrigs-Tib": {
    label: 'Dag yig gsar bsgrigs',
    about: 'Dag yig gsar bsgrigs (New orthography)|Xining: Qinghai min zu chu ban she 民族出版社 / mtsho sngon mi rigs dpe skrun khang (Qinghai Ethnic Publishing House), 1979.',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "sgom_sde_tshig_mdzod_chen_mo": {
    label: 'sGom sde tshig mdzod chen mo',
    about: "sGom sde tshig mdzod chen mo|Author: sGom sde lha rams pa dge bshes thub bstan bsam grub|Sera Jey Monastery, Bylakuppe, Karnataka, India (2005-2016, 4 volumes)||Scan available at archive.org:|- Volume 1: https://archive.org/details/20240428_20240428_0750|- Volume 2: https://archive.org/details/20240428_20240428_0753|- Volume 3: https://archive.org/details/20240428_20240428_0755|- Volume 4: https://archive.org/details/20240428_20240428_0758||- All volumes: https://archive.org/details/bdrc-W2KG209991/bdrc-W2KG209991-1/",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "brda_dkrol_gser_gyi_me_long": {
    label: "Brda dkrol gser gyi me long",
    about: "Brda dkrol gser gyi me long (Golden Mirror for the Elucidation of Words)|Authors: btsan lha ngag dbang tshul khrims|Beijing: Minzu chubanshe 民族出版社 / mi rigs dpe skrun khang (Ethnic Publishing House), 1997. 1,063 pages. ISBN 7105022337.||Scan available at archive.org: https://archive.org/details/bdrc-W28966",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "chos_rnam_kun_btus": {
    label: 'Chos rnam kun btus',
    about: "Chos rnam kun btus: gangs can rig brgya'i chos kyi rnam grangs mthong tshad kun las btus pa ngo mtshar 'phrul gyi lde mig chen po (A Wondrous Great Magical Key: A Comprehensive Compilation of Enumerations of Dharma Terms from the Snow Land’s Hundreds of Fields of Knowledge)|Author: nor brang o rgyan|Beijing: Zhongguo Zangxue chubanshe 中国藏学出版社 / krung go’i bod rig pa dpe skrun khang (China Tibetology Publishing House), 2008, 3 volumes.|Scan available at BDRC: https://library.bdrc.io/show/bdr:MW1KG2733",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "bod_rgya_nang_don_rig_pai_tshig_mdzod": {
    label: "Bod rgya nang don rig pa’i tshig mdzod",
    about: "Bod rgya nang don rig pa’i tshig mdzod (Tibetan–Chinese Dictionary of Buddhist Terminology)|Chengdu: Sichuan minzu chubanshe 四川民族出版社 / si khron mi rigs dpe skrun khang (Sichuan Ethnic Publishing House), 1993 (2 volumes).",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "sngas_rgyas_chos_gzhung_tshig_mdzod": {
    label: 'Bod rgya sangs rgyas chos gzhung gi tshig mdzod',
    about: 'Zang Han fo xue ci dian 藏汉佛学词典 = bod rgya sangs rgyas chos gzhung gi tshig mdzod.|(Chinese-Tibetan dictionary of Buddhist Terms).|Author:  Wang Yinuan and ’jam dpal phun tshogs.|Xining: Qinghai min zu chu ban she 民族出版社 / mtsho sngon mi rigs dpe skrun khang (Qinghai Ethnic Publishing House), 1986 (reprint: 1988). 534 pages.|Scan available at archive.org: https://archive.org/details/bdrc-W22164',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "gangs_can_mkhas_grub_rim_byon_ming_mdzod": {
    label: "Gangs can mkhas grub rim byon ming mdzod",
    about: "Gangs can mkhas grub rim byon ming mdzod (Dictionary of the Generations of Scholars and Accomplished Ones of the Glacier Land)|Lanzhou: Gansu minzu chubanshe 甘肃民族出版社 / kan su'u mi rigs dpe skrun khang (Gansu Ethnic Publishing House), 1992, ISBN 7542102001, 1933 pages",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "bod_yig_tshig_gter_rgya_mtsho": {
    label: "Bod yig tshig gter rgya mtsho",
    about: "Bod yig tshig gter rgya mtsho (Ocean of Treasure of Tibetan Letters)|Author: thub bstan phun tshogs|Chengdu: Sichuan minzu chubanshe 四川民族出版社 / si khron mi rigs dpe skrun khang (Sichuan Ethnic Publishing House), 2012, ISBN 7540949422, 3 volumes (4013 pages)",
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "li_shii_gur_khang": {
    label: "Li shi'i gur khang",
    about: 'Brda gsar rnying gi rnam gzhag li shi\'i gur khang (Presentation of Old and New Terminology: House of Cloves)|Author: skyogs ston rin chen bkra shis (born ca. 1400)||Scan available at archive.org: https://archive.org/details/bdrc-W3CN1669',
    public: true,
    listCredits: true,
    language: ["tib"]
  },
  "LobsangMonlam-Tibetan": {
    label: 'Lobsang Monlam',
    about: 'Lobsang Monlam Dictionary|These entries were taken from the Lobsang Monlam\'s dictionary. See http://www.monlamit.org/ for the original program and https://github.com/iamironrabbit/monlam-dictionary for the code and database..',
    public: false,
    listCredits: false,
    language: ["tib"]
  },
  "FPMT-middling-lamrim-Def": {
    label: 'FPMT Glossary for Middling Lamrim (work in progress): Definitions',
    about: 'FPMT Glossary for Middling Lamrim|This glossary of Tibetan definitions was compiled by the FPMT and includes key technical terms for Tsong-kha-pa\'s Middling Lamrim',
    public: false,
    listCredits: true,
    language: ["tib"]
  },

  /** Sanskrit */
  "Hopkins-Skt2015": {
    label: 'Hopkins Sanskrit 2015',
    about: 'Source: The Uma Institute for Tibetan Studies Tibetan-Sanskrit-English Dictionary (Version: June 2015)|Jeffrey Hopkins, Editor.|Paul Hackett, Contributor and Technical Editor.| Contributors: Nathaniel Garson, William Magee, Andres Montano, John Powers, Craig Preston, Joe Wilson, Jongbok Yi|A PDF version of this dictionary is available for download at: www.uma-tibet.org',
    language: ["tib", "<->", "skt"],
    public: true,
    abbreviations: 'Hopkins',
  },
  "84000Skt": {
    label: '84000 Glossary, Sans&shy;krit',
    about: 'Sanskrit terms from the Glossary of the 84000 translation project. http://www.84000.co/',
    abbreviations: '84000',
    highlight: '(&lt;[^ ]*&gt;)',
    public: true,
    language: ["tib", "<->", "skt"]
  },
  "ITLR": {
    highlight: '(&lt;[^ ]*&gt;|Sanskrit: |English: |See also:)',
    label: 'Indo-Tibetan Lexical Resource (ITLR)',
    about: 'The Indo-Tibetan Lexical Resource (https://www.itlr.net) is a collaborative lexical project built around Sanskrit Headwords. It involves a number of scholars from around the world and cooperates with several institutions.|Many thanks go to Dr. Orna Almogi for allowing the ITLR resources to be included in this application, to Prof. Kiyonori Nagasaki for implementing a data export and making the ITLR data available, and to all contributors of the ITLR project.',
    public: true,
    preformattedLinebreaks: true,
    listCredits: true,
    language: ["tib", "<->", "en", "<->", "skt"]
  },
  "_Mahavyutpatti": {
    type: 'group',
    label: 'Mahā&shy;vyutpatti',
    about: 'Multiple resources related to the Mahāvyutpatti:|- A digital Edition of the Mahāvyutpatti by Dharma Drum Buddhist College|- Scan of the critical edition by Ishihama and Fukuda (1989)|- Scan of a critical edition of the sGra-sbyor bam-po gnis-pa (Madhyavyutpatti), edited by Mie Ishikawa (1990)|Note that the scanned versions are not available in the mobile version of this application due to the large file size of the scanned images.',
    public: true,
    language: ["tib", "<->", "skt"],
    items: {
      "Mahavyutpatti-Skt": {
        label: 'Mahā&shy;vyutpatti',
        about: 'Mahāvyutpatti |This digital edition of the Mahāvyutpatti was created by the Glossaries Team at Dharma Drum Buddhist College|See https://glossaries.dila.edu.tw/ for more information.',
        public: true,
        listCredits: true,
        language: ["tib", "<->", "skt"]
      },
      "Mahavyutpatti-Scan-1989": {
        label: 'Mahā&shy;vyutpatti Critical Edition (scanned dictionary)',
        about: "Yumiko Ishihama & Yoichi Fukuda, 'A New Critical Edition of the Mahāvyutpatti:|Sanskrit-Tibetan-Mongolian Dictionary of Buddhist Terminology (新訂翻訳名義大集 Shintei hon'yaku myogi taishu)'.|Studia Tibetica No. 16. Materials for Tibetan-Mongolian Dictionaries Vol. 1.|Tokyo, Japan: The Toyo Bunko Foundation (東洋文庫), 1989. |Scan available at archive.org: https://archive.org/details/ishihama-yumiko-fukuda-yoichi-1989.-a-new-critical-edition-of-the-mahavyutpatti-/.",
        public: true,
        webOnly: true,
        scanId: "mvp1989",
        scanInfo: {
          offset: 0,
          display_pageadjust: -42,
          min_page: 1,
          max_page: 503,
          width: 1250,
          height: 1760
        },
        linkText: "Open scanned page",
        listCredits: true,
        language: ["tib", "<->", "skt", "mn"]
      },
      "sgra-sbyor-bam-po-gnyis-pa": {
        label: 'sGra sbyor bam po gnyis pa (Madhyavyutpatti, scanned dictionary)',
        about: "Mie Ishikawa, A Critical Edition of the sGra sbyor bam po gnyis pa:|An old and basic commentary on the Mahāvyutpatti|Studia Tibetica no. 18, The Toyo Bunko, 1990.",
        public: true,
        webOnly: true,
        scanId: "mvp-comm-1990",
        scanInfo: {
          offset: 0,
          min_page: 1,
          max_page: 78,
          display_pageadjust: -10,
          width: 1755,
          height: 1241
        },
        linkText: "Open scanned page",
        listCredits: true,
        language: ["tib", "<->", "skt"]
      }
    }
  },
  "Yogacharabhumi-glossary": {
    label: 'Yoga&shy;chara&shy;bhumi Glossary',
    about: 'Tibetan-Sanskrit Table of Buddhist Terminology Based on the Yogacarabhumi|MAHONEY, Richard, ed., Tibetan-Sanskrit Buddhist Terminology based on the `Mahāvyutpatti\' and `Yogācārabhūmi\', (Oxford, North Canterbury: Indica et Buddhica, 2004)|See: http://indica-et-buddhica.org/repositorium/dictionaries/tibetan-sanskrit-terms',
    public: true,
    listCredits: true,
    language: ["tib", "<->", "skt"]
  },
  "_LokeshChandra": {
    type: 'group',
    label: 'Lokesh Chandra',
    about: 'Tibetan-Sanskrit Dictionary|Lokesh Chandra|International Academy of Indian Culture and Aditya Prakashan (1992)|A scanned version is available at archive.org: https://archive.org/details/dictionarytibetansanskritdictionarylokeshchandra_78_F',
    public: true,
    language: ["tib", "->", "skt"],
    items: {
      "LokeshChandraSkt": {
        label: 'Lokesh Chandra',
        about: 'Tibetan-Sanskrit Dictionary|Lokesh Chandra|International Academy of Indian Culture and Aditya Prakashan (1992)|A scanned version is available at archive.org: https://archive.org/details/dictionarytibetansanskritdictionarylokeshchandra_78_F',
        public: true,
        listCredits: true,
        language: ["tib", "<->", "skt"]
      },
      "LokeshChandraTib": {
        label: 'Lokesh Chandra (Sanskrit-Tibetan)',
        about: 'Tibetan-Sanskrit Dictionary|Lokesh Chandra|International Academy of Indian Culture and Aditya Prakashan (1992), ISBN 8185689113|A scanned version is available at archive.org: https://archive.org/details/dictionarytibetansanskritdictionarylokeshchandra_78_F',
        public: true,
        language: ["skt", "<->", "tib"]
      }
    }
  },
  "NegiSkt": {
    label: 'Negi',
    about: 'Tibetan-Sanskrit Dictionary|bod skad dang legs sbyar gyi tshig mdzod chen mo|J.S. Negi|Dictionary Unit, Central Institute of Higher Tibetan Studies, Sarnath',
    abbreviations: 'Negi',
    public: true,
    listCredits: true,
    language: ["tib", "->", "skt"]
  }
} as const;

// Create a flattened version of the above dictionary list where groups are removed and their items are added directly
export let DICTLIST: DictListType = {};

for (const [groupKey, groupValue] of Object.entries(GROUPED_DICTLIST)) {
  const entry = groupValue as any;
  if (entry.type === 'group') {
    for (const [itemKey, itemValue] of Object.entries(entry.items || {})) {
      DICTLIST[itemKey] = { ...itemValue as DictEntry, groupId: groupKey };
    }
  } else {
    DICTLIST[groupKey] = entry;
  }
}
